import React from 'react';

export interface WorkerTask {
  id: string;
  type: 'gemma' | 'tesseract' | 'preprocess';
  data: any;
  priority?: number;
  timeout?: number;
}

export interface WorkerResult {
  id: string;
  success: boolean;
  data?: any;
  error?: string;
  processingTime: number;
}

export interface WorkerPoolConfig {
  maxWorkers: number;
  workerTimeout: number;
  enableLogging: boolean;
}

class WorkerWrapper {
  private worker: Worker;
  private busy = false;
  private currentTask: WorkerTask | null = null;
  private startTime = 0;
  
  constructor(
    private workerPath: string,
    private type: WorkerTask['type'],
    private onComplete: (result: WorkerResult) => void,
    private onError: (error: Error) => void
  ) {
    this.worker = new Worker(workerPath, { type: 'module' });
    this.setupListeners();
  }
  
  private setupListeners() {
    this.worker.onmessage = (event) => {
      const processingTime = performance.now() - this.startTime;
      const result: WorkerResult = {
        id: this.currentTask?.id || '',
        success: true,
        data: event.data,
        processingTime,
      };
      
      this.busy = false;
      this.currentTask = null;
      this.onComplete(result);
    };
    
    this.worker.onerror = (error) => {
      this.busy = false;
      this.currentTask = null;
      this.onError(new Error(`Worker error: ${error.message}`));
    };
  }
  
  isBusy(): boolean {
    return this.busy;
  }
  
  getType(): WorkerTask['type'] {
    return this.type;
  }
  
  async process(task: WorkerTask): Promise<void> {
    if (this.busy) {
      throw new Error('Worker is busy');
    }
    
    this.busy = true;
    this.currentTask = task;
    this.startTime = performance.now();
    
    // Use transferable objects for better performance
    if (task.data instanceof ArrayBuffer) {
      this.worker.postMessage({ type: task.type, data: task.data }, [task.data]);
    } else if (task.data && task.data.buffer instanceof ArrayBuffer) {
      this.worker.postMessage({ type: task.type, data: task.data }, [task.data.buffer]);
    } else {
      this.worker.postMessage({ type: task.type, data: task.data });
    }
  }
  
  terminate() {
    this.worker.terminate();
  }
}

export class WorkerPool {
  private workers: Map<string, WorkerWrapper> = new Map();
  private taskQueue: WorkerTask[] = [];
  private resultCallbacks: Map<string, (result: WorkerResult) => void> = new Map();
  private config: WorkerPoolConfig;
  
  constructor(config?: Partial<WorkerPoolConfig>) {
    this.config = {
      maxWorkers: navigator.hardwareConcurrency || 4,
      workerTimeout: 60000, // 60 seconds
      enableLogging: true,
      ...config,
    };
    
    this.initialize();
  }
  
  private initialize() {
    // Pre-create workers for each type
    const workerConfigs = [
      { path: new URL('./gemma.worker.js', import.meta.url), type: 'gemma' as const, count: 2 },
      { path: new URL('./tesseract.worker.js', import.meta.url), type: 'tesseract' as const, count: 2 },
      { path: new URL('./preprocess.worker.js', import.meta.url), type: 'preprocess' as const, count: 1 },
    ];
    
    let workerId = 0;
    for (const config of workerConfigs) {
      for (let i = 0; i < config.count && workerId < this.config.maxWorkers; i++) {
        const worker = new WorkerWrapper(
          config.path.href,
          config.type,
          (result) => this.handleWorkerComplete(result),
          (error) => this.handleWorkerError(error)
        );
        
        this.workers.set(`${config.type}-${i}`, worker);
        workerId++;
      }
    }
    
    if (this.config.enableLogging) {
      console.log(`[WorkerPool] Initialized with ${this.workers.size} workers`);
    }
  }
  
  private handleWorkerComplete(result: WorkerResult) {
    const callback = this.resultCallbacks.get(result.id);
    if (callback) {
      callback(result);
      this.resultCallbacks.delete(result.id);
    }
    
    // Process next task in queue
    this.processNextTask();
  }
  
  private handleWorkerError(error: Error) {
    console.error('[WorkerPool] Worker error:', error);
    // Process next task even on error
    this.processNextTask();
  }
  
  private processNextTask() {
    if (this.taskQueue.length === 0) return;
    
    // Sort by priority (higher priority first)
    this.taskQueue.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    // Find available worker for the task
    for (let i = 0; i < this.taskQueue.length; i++) {
      const task = this.taskQueue[i];
      const availableWorker = this.findAvailableWorker(task.type);
      
      if (availableWorker) {
        this.taskQueue.splice(i, 1);
        availableWorker.process(task).catch((error) => {
          const result: WorkerResult = {
            id: task.id,
            success: false,
            error: error.message,
            processingTime: 0,
          };
          this.handleWorkerComplete(result);
        });
        
        // Set timeout for the task
        if (task.timeout) {
          setTimeout(() => {
            if (this.resultCallbacks.has(task.id)) {
              const result: WorkerResult = {
                id: task.id,
                success: false,
                error: 'Task timeout',
                processingTime: task.timeout!,
              };
              this.handleWorkerComplete(result);
            }
          }, task.timeout);
        }
        
        break;
      }
    }
  }
  
  private findAvailableWorker(type: WorkerTask['type']): WorkerWrapper | null {
    for (const [, worker] of Array.from(this.workers)) {
      if (worker.getType() === type && !worker.isBusy()) {
        return worker;
      }
    }
    return null;
  }
  
  async queueTask(task: WorkerTask): Promise<WorkerResult> {
    return new Promise((resolve) => {
      this.resultCallbacks.set(task.id, resolve);
      
      // Try to process immediately
      const availableWorker = this.findAvailableWorker(task.type);
      if (availableWorker) {
        availableWorker.process(task).catch((error) => {
          const result: WorkerResult = {
            id: task.id,
            success: false,
            error: error.message,
            processingTime: 0,
          };
          resolve(result);
        });
      } else {
        // Add to queue
        this.taskQueue.push(task);
        if (this.config.enableLogging) {
          console.log(`[WorkerPool] Task queued. Queue length: ${this.taskQueue.length}`);
        }
      }
    });
  }
  
  getQueueLength(): number {
    return this.taskQueue.length;
  }
  
  getActiveWorkers(): number {
    let active = 0;
    for (const [, worker] of Array.from(this.workers)) {
      if (worker.isBusy()) active++;
    }
    return active;
  }
  
  getStatus() {
    return {
      totalWorkers: this.workers.size,
      activeWorkers: this.getActiveWorkers(),
      queueLength: this.taskQueue.length,
      workerTypes: {
        gemma: Array.from(this.workers.values()).filter(w => w.getType() === 'gemma').length,
        tesseract: Array.from(this.workers.values()).filter(w => w.getType() === 'tesseract').length,
        preprocess: Array.from(this.workers.values()).filter(w => w.getType() === 'preprocess').length,
      },
    };
  }
  
  terminate() {
    for (const [, worker] of Array.from(this.workers)) {
      worker.terminate();
    }
    this.workers.clear();
    this.taskQueue = [];
    this.resultCallbacks.clear();
  }
}

// Singleton instance
let workerPoolInstance: WorkerPool | null = null;

export function getWorkerPool(config?: Partial<WorkerPoolConfig>): WorkerPool {
  if (!workerPoolInstance) {
    workerPoolInstance = new WorkerPool(config);
  }
  return workerPoolInstance;
}

// React hook for using the worker pool
export function useWorkerPool() {
  const pool = React.useMemo(() => getWorkerPool(), []);
  const [status, setStatus] = React.useState(pool.getStatus());
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setStatus(pool.getStatus());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [pool]);
  
  const processTask = React.useCallback(
    async (type: WorkerTask['type'], data: any, options?: { priority?: number; timeout?: number }) => {
      const task: WorkerTask = {
        id: crypto.randomUUID(),
        type,
        data,
        priority: options?.priority,
        timeout: options?.timeout,
      };
      
      return pool.queueTask(task);
    },
    [pool]
  );
  
  return { processTask, status };
}